import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check, ArrowRight, Loader2, Clock, CheckCircle, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Pricing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [checkingApproval, setCheckingApproval] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "" });
  const [approvedEmail, setApprovedEmail] = useState(null);
  const [planLoading, setPlanLoading] = useState(null);

  useEffect(() => {
    if (location.state?.openSignIn) {
      setIsLogin(true);
      setShowAuth(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const handlePlanClick = async (planId) => {
    if (planId === "institutional") {
      window.open("https://wa.link/og6izz", "_blank", "noopener,noreferrer");
      return;
    }

    setSelectedPlan(planId);

    const token = localStorage.getItem("token");
    if (token) {
      // Already logged in — check waitlist approval by user email
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const email = user.email;
      if (email) {
        setPlanLoading(planId);
        try {
          const res = await axios.get(`${API}/waitlist/check`, {
            params: { email, service: "alphabot" },
          });
          if (res.data.approved) {
            navigate(`/payment/crypto/${planId}`);
            return;
          }
        } catch { /* fall through to waitlist */ }
        finally { setPlanLoading(null); }
      }
    }

    // Not logged in or not approved — show waitlist dialog
    setWaitlistEmail("");
    setWaitlistSuccess(false);
    setShowWaitlist(true);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setWaitlistLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist/join`, {
        email: waitlistEmail,
        service: "alphabot",
      });
      setWaitlistSuccess(true);
      if (res.data.already_exists) {
        toast.info("You're already on the waitlist!");
      } else {
        toast.success("You've been added to the waitlist!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join waitlist");
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleCheckApproval = async () => {
    if (!waitlistEmail) return;
    setCheckingApproval(true);
    try {
      const res = await axios.get(`${API}/waitlist/check`, {
        params: { email: waitlistEmail, service: "alphabot" },
      });
      if (res.data.approved) {
        setApprovedEmail(waitlistEmail);
        setShowWaitlist(false);
        toast.success("You're approved! Sign in or create an account to proceed.");
        setFormData((prev) => ({ ...prev, email: waitlistEmail }));
        setIsLogin(true);
        setShowAuth(true);
      } else if (res.data.on_waitlist) {
        toast.info("Your request is still pending. We'll notify you when approved.");
      } else {
        toast.info("You're not on the waitlist yet. Please join first.");
      }
    } catch {
      toast.error("Failed to check status");
    } finally {
      setCheckingApproval(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/signup";
      const response = await axios.post(`${API}${endpoint}`, formData);
      const token = response.data.token;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      setShowAuth(false);
      if (selectedPlan) {
        navigate(`/payment/crypto/${selectedPlan}`);
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      id: "starter",
      name: "Base",
      price: "$70",
      period: "/month",
      description: "Essential automated trading with AI insights",
      features: [
        "Full Alphabot access",
        "30 AI analysis credits / month",
        "Capital up to $40K",
        "1 MT5 account",
        "MT5 dashboard integration",
        "Email support",
      ],
      cta: "Join Waitlist",
      popular: false,
    },
    {
      id: "pro",
      name: "Pro",
      price: "$110",
      period: "/month",
      description: "For active traders who need more AI power",
      features: [
        "Everything in Base",
        "120 AI analysis credits / month",
        "Capital up to $100K",
        "Up to 3 MT5 accounts",
        "Custom risk settings",
        "Priority 24/7 support",
      ],
      cta: "Join Waitlist",
      popular: true,
    },
    {
      id: "institutional",
      name: "Institutional",
      price: "Custom",
      period: "",
      description: "Tailored solutions for funds, teams, and high-volume traders",
      features: [
        "Custom AI analysis volume",
        "Dedicated account manager",
        "Custom risk & compliance setup",
        "Multi-account management",
        "Priority onboarding & support",
      ],
      cta: "Contact Us",
      popular: false,
      isCustom: true,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar onSignIn={() => { setIsLogin(true); setShowAuth(true); }} />

      <div className="pt-24 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Choose Your Plan
            </h1>
            <p className="text-lg text-[#A1A1AA] mb-2">
              Start profiting with automated trading today
            </p>
            <p className="text-sm text-[#71717A]">
              Join the waitlist to get early access and payment instructions
            </p>
            {!localStorage.getItem('token') && (
              <button
                onClick={() => { setIsLogin(true); setShowAuth(true); }}
                className="mt-4 text-sm text-[#00E676] hover:underline font-medium"
              >
                Already have an account? Sign In
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {plans.map((plan) => (
              <div
                key={plan.name}
                data-testid={`plan-${plan.name.toLowerCase()}`}
                className={`bg-[#111111] border rounded-lg p-5 sm:p-8 relative ${
                  plan.popular
                    ? "border-[#00E676] shadow-lg shadow-[#00E676]/20"
                    : "border-[#27272A]"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-[#00E676] text-black px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-[#A1A1AA] text-sm">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <span className="text-3xl sm:text-5xl font-bold font-data">
                    {plan.price}
                  </span>
                  <span className="text-[#A1A1AA]">{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#00E676] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#E5E5E5]">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`btn-${plan.name.toLowerCase()}`}
                  onClick={() => handlePlanClick(plan.id)}
                  disabled={planLoading === plan.id}
                  className={`w-full ${
                    plan.popular
                      ? "bg-[#00E676] hover:bg-[#00C853] text-black"
                      : plan.isCustom
                      ? "bg-[#007AFF]/10 hover:bg-[#007AFF]/20 text-[#007AFF] border border-[#007AFF]/30"
                      : "bg-[#1C1C1E] hover:bg-[#27272A] text-white"
                  } rounded-md`}
                >
                  {planLoading === plan.id ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      {plan.isCustom ? (
                        <Mail className="mr-2 w-4 h-4" />
                      ) : (
                        <Clock className="mr-2 w-4 h-4" />
                      )}
                      {plan.cta}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>

          <div className="text-center mt-6">
            <p className="text-sm text-[#71717A]">
              Secure crypto payments (USDT & BTC) available after waitlist approval
            </p>
          </div>
        </div>
      </div>

      {/* Waitlist Dialog */}
      <Dialog open={showWaitlist} onOpenChange={setShowWaitlist}>
        <DialogContent className="bg-[#111111] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {waitlistSuccess ? "You're on the list!" : "Request to Join Waitlist"}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {waitlistSuccess
                ? "We'll review your request and notify you when you're approved to proceed with payment."
                : `Enter your email to join the waitlist for the ${
                    plans.find((p) => p.id === selectedPlan)?.name || ""
                  } plan.`}
            </DialogDescription>
          </DialogHeader>

          {!waitlistSuccess ? (
            <form onSubmit={handleWaitlistSubmit} className="space-y-4">
              <div>
                <Label htmlFor="waitlist-email" className="text-[#E5E5E5]">
                  Email Address
                </Label>
                <Input
                  id="waitlist-email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  className="bg-[#1C1C1E] border-[#27272A] text-white"
                />
              </div>
              <Button
                type="submit"
                disabled={waitlistLoading}
                className="w-full bg-[#00E676] hover:bg-[#00C853] text-black"
              >
                {waitlistLoading ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Clock className="mr-2 w-4 h-4" />
                    Join Waitlist
                  </>
                )}
              </Button>

              <div className="border-t border-[#27272A] pt-4">
                <p className="text-xs text-[#71717A] text-center mb-3">
                  Already approved? Check your status
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCheckApproval}
                  disabled={checkingApproval || !waitlistEmail}
                  className="w-full border-[#27272A] text-white hover:bg-[#1C1C1E]"
                >
                  {checkingApproval ? (
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="mr-2 w-4 h-4 text-[#00E676]" />
                  )}
                  Check Approval Status
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-[#00E676]/10 border border-[#00E676]/30 rounded-lg p-4 text-center">
                <CheckCircle className="w-12 h-12 text-[#00E676] mx-auto mb-3" />
                <p className="text-sm text-[#E5E5E5]">
                  We've received your request for <strong>{waitlistEmail}</strong>. You'll be able to proceed with USDT/BTC payment once approved.
                </p>
              </div>
              <Button
                onClick={handleCheckApproval}
                disabled={checkingApproval}
                className="w-full bg-[#1C1C1E] hover:bg-[#27272A] text-white"
              >
                {checkingApproval ? (
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 w-4 h-4" />
                )}
                Check If Approved
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowWaitlist(false);
                  setWaitlistSuccess(false);
                }}
                className="w-full text-[#A1A1AA] hover:text-white"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auth Dialog — shown only after waitlist approval */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-[#111111] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isLogin ? "Welcome Back" : "Create Account"}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {isLogin
                ? "Sign in to complete your purchase"
                : "Sign up to get started with your plan"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAuth} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-[#E5E5E5]">
                  Name
                </Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  type="text"
                  required={!isLogin}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="bg-[#1C1C1E] border-[#27272A] text-white"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-[#E5E5E5]">
                Email
              </Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="bg-[#1C1C1E] border-[#27272A] text-white"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#E5E5E5]">
                Password
              </Label>
              <Input
                id="password"
                data-testid="auth-password-input"
                type="password"
                required
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                className="bg-[#1C1C1E] border-[#27272A] text-white"
              />
            </div>

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00E676] hover:bg-[#00C853] text-black"
            >
              {loading
                ? "Please wait..."
                : isLogin
                ? "Sign In"
                : "Create Account"}
            </Button>

            <p className="text-center text-sm text-[#A1A1AA]">
              {isLogin
                ? "Don't have an account?"
                : "Already have an account?"}
              <button
                type="button"
                data-testid="auth-toggle-btn"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#00E676] hover:underline ml-2"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
